'use client';

import React from 'react';
import styled from 'styled-components';
import { Text } from '@lmn/design-system';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { useFetchHotelStatic } from '@/app/components/hotel-detail/hooks/useFetchHotelStatic';
import useSelectedTransport from '@/app/components/hotel-detail/hooks/useSelectedTransport';
import { getTransportDistance, mapTransportDistance } from '@/app/components/hotel-detail/common/transportDistance';
import { getDistanceFromM } from '@/app/common/measures/getters';
import { useTranslation } from '@/app/components/translations/Translation';
import { useHotelMapModal } from '@/app/components/hotel-detail/HotelMapModalProvider';
import { trackViewMap } from '@/app/components/hotel-detail/trackInteractionEvents';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { SeeAllButton } from '@/app/components/hotel-detail/common/button';
import { Icon } from '@/app/common/icons/Icon';

/* No row gap: each boundary below states its own spacing, so the title-to-content gap is a single
   declaration on TitleRow rather than a margin topped up by a container gap. That is what lets this
   column's first POI row land on the same line as the check-in block beside it in TwoColBlock —
   CheckinCheckoutTitle states the same gap the same way. (MPH-839) */
const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

/* Owns the whole title-to-content gap: space150 for mobile and tablet alike in every role — the
   design's single title-to-content gap for every block in the modal, with no tablet step, so the
   switch is at lg rather than sm. From lg the two sub-block roles (the Overview column, Property
   surroundings) take space200, the same value on the same kind of property as CheckinCheckoutTitle
   beside them, so the two columns' first content rows land on the same line. The tab-section role
   stays on space150: the designs pair the Location tab's section-size heading with the tighter gap.
   That tab only renders below lg anyway, so the branch matters only when the tab set falls back to
   its UA guess on a wide viewport. It sits on the row rather than on SectionTitle so the title's
   own centring inside it stays a purely horizontal concern. (MPH-839) */
const TitleRow = styled.div<{ $asTabSection?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: ${({ theme }) => theme.kallisto.space.space150};

  @media ${customCss.breakpoints.lg} {
    margin-bottom: ${({ theme, $asTabSection }) =>
      $asTabSection ? theme.kallisto.space.space150 : theme.kallisto.space.space200};
  }
`;

/* Title4 is the sub-block size, taken only from lg — that is where TwoColBlock puts this beside
   another block. Up to lg it is a full-width mobile block and keeps Title3, which is also what the
   Location tab needs: that tab's set is the mobile one below lg, so its heading sits next to
   Policies, Reviews and Facilities headings that are all Title3. The tab-section role keeps Title3
   past lg too, for the case where the tab set falls back to its UA guess on a wide viewport and the
   Location tab renders there anyway. (MPH-839) */
const SectionTitle = styled(Text).attrs({ variant: 'Title4', color: 'textPrimary' })<{
  $asTabSection?: boolean;
}>`
  ${({ theme }) => theme.kallisto.mixins.font('Title3')};

  @media ${customCss.breakpoints.lg} {
    ${({ theme, $asTabSection }) => !$asTabSection && theme.kallisto.mixins.font('Title4')};
  }
`;

const PoiList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PoiRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const PoiLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const PoiText = styled(Text).attrs({ variant: 'Body', color: 'textPrimary' })`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Disclaimer = styled(Text).attrs({ variant: 'BodySmall', color: 'textSecondary' })`
  margin-top: ${({ theme }) => theme.kallisto.space.space150};
`;

const WhatsAroundSeeAll = styled(SeeAllButton)`
  margin-top: ${({ theme }) => theme.kallisto.space.space100};
`;

function useDistanceToTransport() {
  const { data: transportData } = useSelectedTransport();
  const { accommodationOnly } = useUrlSearchParams();

  if (!transportData || accommodationOnly) return null;
  const distancesData = mapTransportDistance(getTransportDistance(transportData));
  const destinationCode = transportData?.transports?.[0]?.outbound?.destination;
  return distancesData ? (distancesData.find((d) => d.code === destinationCode) ?? null) : null;
}

// Whether EvoWhatsAround will render anything — lets parents skip surrounding
// dividers/columns when it won't.
export function useHasWhatsAround() {
  const { data: staticData } = useFetchHotelStatic();
  const distanceToTransport = useDistanceToTransport();
  return !!staticData?.distanceToCityCenter || !!distanceToTransport?.distanceWithUnit?.value;
}

// `onSeeAll` overrides what the "See all" link does — the desktop details modal passes a handler
// that smooth-scrolls to the Property surroundings section. Without it the link opens the map modal
// directly (the current mobile behaviour). `hideSeeAll` drops the link entirely, and `hideIcons`
// drops the leading POI icons — both used by the copy rendered inside the Property surroundings
// section (a plain, icon-less list).
// `asTabSection` marks the one place this block IS a tab's section title (the mobile Location tab)
// rather than a heading inside a column: its title keeps the section Title3 size at every width and
// the tighter space150 gap below it, which is the pairing the designs specify there. It cannot be
// inferred from hideSeeAll/hideIcons: Property surroundings passes those identically and wants the
// sub-block size and spacing (MPH-839).
export function EvoWhatsAround({
  onSeeAll,
  hideSeeAll = false,
  hideIcons = false,
  asTabSection = false,
}: {
  onSeeAll?: () => void;
  hideSeeAll?: boolean;
  hideIcons?: boolean;
  asTabSection?: boolean;
} = {}) {
  const { localization } = usePageWrapperData();
  const { data: staticData } = useFetchHotelStatic();
  const distanceToTransport = useDistanceToTransport();
  const { isTrain } = useUrlSearchParams();
  const { t } = useTranslation();
  const { showMap } = useHotelMapModal();
  const { sendCuriosityClickEvent } = useCuriosity();

  const openMap = () => {
    showMap();
    trackViewMap();
    sendCuriosityClickEvent({
      event_name: 'click_view_map_from_hotel_info',
      event_context: 'from_hotel_info',
    });
  };

  const hasCityDistance = !!staticData?.distanceToCityCenter;
  const hasTransportDistance = !!distanceToTransport?.distanceWithUnit?.value;

  if (!hasCityDistance && !hasTransportDistance) return null;

  return (
    <Container>
      <TitleRow $asTabSection={asTabSection}>
        <SectionTitle $asTabSection={asTabSection}>{t('vacation.select.details.whatsaround.title')}</SectionTitle>
      </TitleRow>
      <PoiList>
        {hasCityDistance && staticData?.distanceToCityCenter && (
          <PoiRow>
            <PoiLeft>
              {!hideIcons && (
                <Icon
                  category="Location"
                  name="icon-flag-point"
                  size={20}
                  color="iconSecondary"
                  alt=""
                  css="flex-shrink: 0;"
                />
              )}
              <PoiText>
                {t(
                  staticData.city
                    ? 'vacation.select.details.distancehotelcitycenter2.title'
                    : 'vacation.select.details.distancehotelcitycenter.title',
                  {
                    distance: getDistanceFromM(staticData.distanceToCityCenter, localization).value?.toString(),
                    unit: getDistanceFromM(staticData.distanceToCityCenter, localization).unit,
                    city: staticData.city || '',
                  }
                )}
              </PoiText>
            </PoiLeft>
          </PoiRow>
        )}
        {hasTransportDistance && distanceToTransport?.distanceWithUnit && (
          <PoiRow>
            <PoiLeft>
              {!hideIcons && (
                <Icon
                  category="Transportation"
                  name={isTrain ? 'icon-train' : 'icon-airplane-up-right'}
                  size={20}
                  color="iconSecondary"
                  alt=""
                  css="flex-shrink: 0;"
                />
              )}
              <PoiText>
                {t(
                  isTrain
                    ? 'vacation.select.details.distancestationhotel.title'
                    : 'vacation.select.details.distanceairporthotel.title',
                  {
                    distance: distanceToTransport.distanceWithUnit.value?.toString(),
                    unit: distanceToTransport.distanceWithUnit.unit,
                  }
                )}
              </PoiText>
            </PoiLeft>
          </PoiRow>
        )}
      </PoiList>
      <Disclaimer>{t('vacation.select.details.distances.disclaimer')}</Disclaimer>
      {!hideSeeAll && (
        <WhatsAroundSeeAll onClick={onSeeAll ?? openMap}>
          {t('vacation.select.cc.hotel_info.location.see_all')}
        </WhatsAroundSeeAll>
      )}
    </Container>
  );
}
